/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from 'react';
import {
  GDSStackLayoutProps,
  GDSStyleObject,
  queryHelpers,
  StackItem,
  StackLayout,
  useMediaQuery,
} from '@leagueplatform/genesis-core';
import {
  EVENT_NAME,
  PRODUCT_AREA,
  trackAnalyticsEvent,
} from '@leagueplatform/analytics';
import {
  ErrorState as WebCommonErrorState,
  ErrorStateProps as WebCommonErrorStateProps,
} from '@leagueplatform/web-common-components';
import {
  ERROR_TYPES,
  useErrorContent,
} from 'hooks/use-error-content/use-error-content.hook';
import { FullScreenContainer } from 'components/full-screen-container/full-screen-container.component';
import { usePatientPortalUIConfigOptions } from 'utils/patient-portal-ui-config';
import {
  ANALYTICS_SCREEN_NAMES,
  SUB_PRODUCT_AREA_NAMES,
} from '../../common/constants';

export type ErrorStateProps = WebCommonErrorStateProps & {
  isFullPage?: boolean;
  styleProps?: Partial<GDSStackLayoutProps>;
  errorType?: ERROR_TYPES;
  hideButton?: boolean;
  headingString?: string;
  bodyString?: string;
  buttonText?: string;
  buttonAction?: () => void;
};

export const ErrorState = ({
  styleProps,
  isFullPage = false,
  hideButton = false,
  imageSrc,
  ...errorData
}: ErrorStateProps) => {
  const errorContent = useErrorContent(errorData);
  const { buttonPriority } = errorData;
  const isMobile = useMediaQuery(queryHelpers.down('tablet'));
  const CONTAINER_WIDTH = '708px';
  const { errorState } = usePatientPortalUIConfigOptions();
  const BUTTON_MAX_WIDTH = '328px';

  const baseCss: GDSStyleObject = {
    p: { color: '$onSurfaceTextSubdued' },
    button: {
      padding: '$threeQuarters $two',
    },
    '.error-state-button-wrapper': {
      maxWidth: BUTTON_MAX_WIDTH,
      width: '100%',
      button: {
        width: 'inherit',
        justifyContent: 'center',
      },
    },
  };

  // TODO: Update this as part of analytics
  useEffect(() => {
    trackAnalyticsEvent(EVENT_NAME.SCREEN_LOADED, {
      product_area: PRODUCT_AREA.LOGIN,
      sub_product_area: SUB_PRODUCT_AREA_NAMES.SIGN_IN,
      screen_name: ANALYTICS_SCREEN_NAMES.LOGIN_ERROR,
    });
  }, []);

  if (isFullPage) {
    return (
      <FullScreenContainer
        horizontalAlignment="center"
        css={{
          backgroundColor: isMobile
            ? 'transparent'
            : errorState.backgroundColor,
        }}
      >
        <StackLayout
          verticalAlignment="center"
          {...styleProps}
          css={{
            ...baseCss,
            textAlign: 'center',
            maxWidth: isMobile ? '350px' : 'none',
            ...styleProps?.css,
          }}
        >
          <StackItem
            css={{
              background: '$surfaceCardSecondary',
              padding: isMobile ? '$oneAndHalf' : '$two',
              width: isMobile ? '100%' : CONTAINER_WIDTH,
            }}
          >
            <WebCommonErrorState
              hideButton={hideButton}
              imageSrc={imageSrc || errorContent.imageSrc}
              buttonPriority={buttonPriority}
              {...errorContent}
            />
          </StackItem>
        </StackLayout>
      </FullScreenContainer>
    );
  }

  return (
    <StackLayout
      {...styleProps}
      css={{
        ...baseCss,
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        ...styleProps?.css,
      }}
    >
      <WebCommonErrorState
        hideButton={hideButton}
        imageSrc={imageSrc}
        buttonPriority={buttonPriority}
        {...errorContent}
      />
    </StackLayout>
  );
};
